import { Component, inject, signal, type WritableSignal } from '@angular/core';
import { Navbar } from '../../../../shared/components/navbar/navbar';
import { Card } from '../../../../shared/components/card/card/card';
import { CardHeader } from '../../../../shared/components/card/card-header/card-header';
import { TablePaymentsTopFive } from '../../../../shared/tables/table-payments-top-five/table-payments-top-five';
import { TablePayments } from '../../../../shared/tables/table-payments/table-payments';
import { DialogModule } from 'primeng/dialog';
import { CommonModule, formatCurrency } from '@angular/common';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  type FormGroup,
} from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { DatePickerModule } from 'primeng/datepicker';
import {
  Payment,
  PaymentCategory,
  PaymentStatus,
  PaymentType,
  type CreatePaymentPayload,
  type PaymentMetrics,
  type PaymentDueMetrics,
  type PaymentEvolution,
  type PaymentEvolutionMonth,
  type PaymentTodayIndicators,
  type TotalPaymentsMetrics,
  type PaymentDuplicatedErrorData,
  type PaymentDuplicatedError,
  PaymentFilterDateField,
} from '../../../../interfaces/payment';
import { ButtonModule } from 'primeng/button';
import type { People } from '../../../../interfaces/people';
import { ContractStatus, type Contract } from '../../../../interfaces/contract';
import { PeopleService } from '../../../../services/people.service';
import { ContractService } from '../../../../services/contract.service';
import { PaymentCategoryService } from '../../../../services/payment-category.service';
import { InputNumberModule } from 'primeng/inputnumber';
import { PaymentTypePipe } from '../../../../shared/pipes/payment-type-pipe';
import dayjs, { type Dayjs } from 'dayjs';
import { MessageService } from 'primeng/api';
import { finalize, forkJoin } from 'rxjs';
import type { HttpErrorResponse } from '@angular/common/http';
import { PaymentService } from '../../../../services/payment.service';
import { SkeletonModule } from 'primeng/skeleton';
import { FileInputDirective } from '@ngx-dropzone/cdk';
import { TooltipModule } from 'primeng/tooltip';
import { FloatLabelModule } from 'primeng/floatlabel';
import { MultiSelectModule } from 'primeng/multiselect';
import { PaymentStatusPipe } from '../../../../shared/pipes/payment-status-pipe';
import { SimpleHorizontalBar } from '../../../../shared/components/simple-horizontal-bar/simple-horizontal-bar';
import { DividerModule } from 'primeng/divider';
import type { ChartConfiguration } from 'chart.js';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { UIChart } from 'primeng/chart';
import { EmptyChart } from '../../../../shared/components/empty-chart/empty-chart';
import {
  barChartDefaultOptions,
  chartColors,
  doughnutChartDefaultOptions,
} from '../../../../interfaces/charts';
import { HeaderCard } from '../../../../shared/components/header-card/header-card';
import { TabsModule } from 'primeng/tabs';
import type { User } from '../../../../interfaces/user';
import { UserService } from '../../../../services/user.service';
import { InputMaskModule } from 'primeng/inputmask';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { PopoverModule } from 'primeng/popover';
import { PaymentFilterDateFieldPipe } from '../../../../shared/pipes/payment-filter-date-field-pipe';
import { PaymentCard } from '../components/payment-card/payment-card';

@Component({
  selector: 'app-payments-list',
  imports: [
    CommonModule,
    Navbar,
    Card,
    CardHeader,
    TablePaymentsTopFive,
    TablePayments,
    DialogModule,
    ReactiveFormsModule,
    SelectModule,
    InputTextModule,
    InputNumberModule,
    MessageModule,
    DatePickerModule,
    ButtonModule,
    PaymentTypePipe,
    SkeletonModule,
    TooltipModule,
    FloatLabelModule,
    PaymentStatusPipe,
    MultiSelectModule,
    FormsModule,
    SimpleHorizontalBar,
    DividerModule,
    ProgressSpinnerModule,
    UIChart,
    EmptyChart,
    TabsModule,
    InputMaskModule,
    InputGroupModule,
    InputGroupAddonModule,
    PaymentFilterDateFieldPipe,
    PopoverModule,
    PaymentCard,
  ],
  templateUrl: './payments-list.html',
  styleUrl: './payments-list.css',
})
export class PaymentsList {
  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _messageService: MessageService = inject(MessageService);
  private readonly _peopleService: PeopleService = inject(PeopleService);
  private readonly _contractService: ContractService = inject(ContractService);
  private readonly _paymentService: PaymentService = inject(PaymentService);
  private readonly _userService: UserService = inject(UserService);
  private readonly _paymentCategoryService: PaymentCategoryService = inject(PaymentCategoryService);

  public user: WritableSignal<User | null> = signal<User | null>(null);

  public loading: WritableSignal<boolean> = signal(false);
  public loadingDashboard: WritableSignal<boolean> = signal(false);

  public isInvoiceDragging: WritableSignal<boolean> = signal(false);
  public isDocumentDragging: WritableSignal<boolean> = signal(false);

  public paymentMetrics: WritableSignal<PaymentMetrics | null> = signal<PaymentMetrics | null>(
    null
  );
  public paymentDueMetrics: WritableSignal<PaymentDueMetrics | null> =
    signal<PaymentDueMetrics | null>(null);
  public paymentEvolution: WritableSignal<PaymentEvolution | null> =
    signal<PaymentEvolution | null>(null);
  public paymentTodayIndicators: WritableSignal<PaymentTodayIndicators | null> =
    signal<PaymentTodayIndicators | null>(null);
  public totalPaymentMetrics: WritableSignal<TotalPaymentsMetrics | null> =
    signal<TotalPaymentsMetrics | null>(null);

  public totalPaymentMetricsChartData: WritableSignal<any> = signal<any>(null);
  public totalPaymentMetricsChartOptions: WritableSignal<ChartConfiguration['options']> = signal<
    ChartConfiguration['options']
  >({ ...doughnutChartDefaultOptions });

  public paymentEvolutionChartData: WritableSignal<any> = signal<any>(null);
  public paymentEvolutionChartOptions: WritableSignal<ChartConfiguration['options']> = signal<
    ChartConfiguration['options']
  >({ ...barChartDefaultOptions });

  public paymentByCategoryChartData: WritableSignal<any> = signal<any>(null);
  public paymentByCategoryChartOptions: WritableSignal<ChartConfiguration['options']> = signal<
    ChartConfiguration['options']
  >({ ...doughnutChartDefaultOptions });

  public paymentByCategory: WritableSignal<any | null> = signal<any | null>(null);

  public paymentDialog: boolean = false;
  public paymentDialogTitle: WritableSignal<string> = signal<string>('');

  public paymentConfirmDialog: boolean = false;
  public paymentConfirmType: 'warning' | 'error' = 'error';
  public paymentConfirmData: PaymentDuplicatedErrorData[] = [];

  public peopleList: WritableSignal<People[]> = signal<People[]>([]);
  public contractList: WritableSignal<Contract[]> = signal<Contract[]>([]);
  public categoryList: WritableSignal<PaymentCategory[]> = signal<PaymentCategory[]>([]);
  public subcategoryList: WritableSignal<PaymentCategory[]> = signal<PaymentCategory[]>([]);

  public paymentType: typeof PaymentType = PaymentType;
  public paymentStatus: typeof PaymentStatus = PaymentStatus;
  public paymentFilterDateField: typeof PaymentFilterDateField = PaymentFilterDateField;

  public paymentForm: FormGroup = this._fb.group({
    id: [null],
    contract_id: [null, [Validators.required]],
    receiver_id: [null, [Validators.required]],
    category_id: [null, [Validators.required]],
    subcategory_id: [null],
    nfse_key: [null, [Validators.required]],
    title: [null, [Validators.required]],
    due_date: [null, [Validators.required]],
    paid_at: [null],
    issue_at: [dayjs().toDate(), [Validators.required]],
    value: [null, [Validators.required]],
    reference_month: [null, [Validators.required]],
    type: [null, [Validators.required]],
    invoice: [null, [Validators.required]],
    is_from_invoice: [false],
    invoice_docs: [[]],
    status: [null],
    invoice_url: [null],
    invoice_value: [null],
    invoice_pending_value: [null],
    docs: [[]],
  });

  public paymentFiltersForm: FormGroup = this._fb.group({
    search_term: [null],
    category_id: [[]],
    people_id: [[]],
    status: [[]],
    date_range: [[dayjs().startOf('month').toDate(), dayjs().endOf('month').toDate()]],
    date_field: [PaymentFilterDateField.DUE_DATE],
  });

  get paymentId(): string {
    return this.paymentForm.get('id')?.value;
  }

  get status(): string {
    return this.paymentForm.get('status')?.value;
  }

  get paymentInvoice(): any {
    return this.paymentForm.get('invoice')?.value;
  }

  private _asArray<T>(value: T[] | T | null | undefined): T[] {
    if (Array.isArray(value)) {
      return value;
    }

    return value ? [value] : [];
  }

  get isFromInvoice(): boolean {
    return !!this.paymentForm.get('is_from_invoice')?.value;
  }

  get invoiceDocs(): any[] {
    return this._asArray(this.paymentForm.get('invoice_docs')?.value);
  }

  get paymentDocs(): any[] {
    return this._asArray(this.paymentForm.get('docs')?.value);
  }

  get dateField(): PaymentFilterDateField {
    return this.paymentFiltersForm.get('date_field')?.value;
  }

  public ngOnInit(): void {
    this._getUser();
    this._initDashboard();
    this.getPeopleList();
    this.getPaymentCategories();
  }

  private _toggleLoading(): void {
    this.loading.set(!this.loading());
  }

  private _toggleLoadingDashboard(): void {
    this.loadingDashboard.set(!this.loadingDashboard());
  }

  private _initDashboard(): void {
    this._toggleLoadingDashboard();

    forkJoin([
      this._paymentService.metrics(),
      this._paymentService.dueMetrics(),
      this._paymentService.byCategory(),
      this._paymentService.paymentsEvolution(),
      this._paymentService.todayIndicators(),
      this._paymentService.totalPaymentsMetrics(),
    ])
      .pipe(finalize(() => this._toggleLoadingDashboard()))
      .subscribe({
        next: ([
          metrics,
          dueMetrics,
          byCategory,
          paymentsEvolution,
          todayIndicators,
          totalPaymentsMetrics,
        ]) => {
          this.paymentMetrics.set(metrics.data);
          this.paymentDueMetrics.set(dueMetrics.data);
          this.paymentByCategory.set(byCategory.data);
          this.paymentEvolution.set(paymentsEvolution.data);
          this.paymentTodayIndicators.set(todayIndicators.data);
          this.totalPaymentMetrics.set(totalPaymentsMetrics.data);

          this.mountPaymentByCategoryData();

          this.mountTotalPaymentMetricData();
          this.mountTotalPaymentMetricOptions();

          this.mountPaymentEvolutionData();
          this.mountPaymentEvolutionOptions();
        },
      });
  }

  private _getUser(): void {
    this._userService.me().subscribe({
      next: (response) => {
        if (response.success) {
          this.user.set(response.data);
        }
      },
    });
  }

  public userHasPermission(permission: string): boolean {
    return (
      this.user()?.roles.some((role) => role.permissions.some((p) => p.name === permission)) ||
      false
    );
  }

  public fromPaymentFiltersForm(key: string): string {
    if (key === 'start_date') {
      return dayjs(this.paymentFiltersForm.get('date_range')?.value[0]).format('YYYY-MM-DD');
    }

    if (key === 'end_date') {
      return dayjs(this.paymentFiltersForm.get('date_range')?.value[1]).format('YYYY-MM-DD');
    }

    return this.paymentFiltersForm.get(key)?.value;
  }

  private _resetPaymentForm(): void {
    this.paymentForm.reset();
    this.paymentForm.enable();
    this.paymentForm.patchValue({
      invoice: null,
      is_from_invoice: false,
      invoice_docs: [],
      issue_at: dayjs().toDate(),
      docs: [],
    });
  }

  public closeDialog(): void {
    this.paymentDialog = false;
    this._resetPaymentForm();
  }

  public openDialog(payment?: Payment): void {
    this._resetPaymentForm();
    this.paymentDialog = true;

    if (payment) {
      const paymentData = payment as Payment & {
        invoice_docs?: unknown;
        is_from_invoice?: boolean;
      };

      this.paymentForm.patchValue({
        ...paymentData,
        docs: this._asArray(payment.docs),
        invoice_docs: this._asArray(paymentData.invoice_docs),
        is_from_invoice: !!paymentData.is_from_invoice,
        issue_at: payment.issue_at ? dayjs(payment.issue_at).toDate() : null,
        due_date: payment.due_date ? dayjs(payment.due_date).toDate() : null,
        paid_at: payment.paid_at ? dayjs(payment.paid_at).toDate() : null,
      });

      this.paymentDialogTitle.set('Atualizar Pagamento');

      if (payment.category_id) {
        this.categoryList.update((list) => [...list, payment.category!]);
        this.getPaymentCategories(payment.category_id);
      }

      if (!this.peopleList().some((person) => person.id === payment.receiver_id)) {
        this.peopleList.update((list) => [...list, payment.receiver!]);
      }

      if (!this.contractList().some((contract) => contract.id === payment.contract_id)) {
        this.contractList.update((list) => [...list, payment.contract!]);
      }

      this.paymentForm.disable();
      return;
    }

    this.paymentDialogTitle.set('Criar Pagamento');
  }

  public openConfirmDialog(payload: PaymentDuplicatedError): void {
    this.paymentConfirmType = payload.allow_if_different_nfse_key ? 'warning' : 'error';
    this.paymentConfirmData = this._asArray(payload.duplicates);
    this.paymentConfirmDialog = true;
  }

  public closeConfirmDialog(): void {
    this.paymentConfirmDialog = false;
    this.paymentConfirmType = 'error';
    this.paymentConfirmData = [];
  }

  public submitDialog(force: boolean = false): void {
    if (!this.paymentForm.valid) {
      this.paymentForm.markAllAsDirty();
      this._messageService.add({
        severity: 'warn',
        summary: 'Preencha todos os campos obrigatórios',
        life: 3000,
      });

      return;
    }

    const data = this.paymentForm.getRawValue();
    if (data.id) {
      this._edit(data.id, data);
      return;
    }

    this._create(data, force);
  }

  private _create(payment: CreatePaymentPayload, force: boolean = false): void {
    this._toggleLoading();

    this._paymentService
      .post({
        ...payment,
        force_duplicate: force,
      })
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this._messageService.add({
              severity: 'success',
              summary: response.message,
              life: 3000,
            });

            this.closeDialog();
          }
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 409) {
            this.openConfirmDialog(error.error.data as PaymentDuplicatedError);
            return;
          }

          this._messageService.add({
            severity: 'error',
            summary: 'Falha ao criar um pagamento',
            detail: error.error.message,
            life: 3000,
          });
        },
      });
  }

  private _edit(id: string, payment: CreatePaymentPayload): void {
    this._toggleLoading();

    this._paymentService
      .put(id, payment)
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this._messageService.add({
              severity: 'success',
              summary: response.message,
              life: 3000,
            });

            this.closeDialog();
          }
        },
        error: (error: HttpErrorResponse) =>
          this._messageService.add({
            severity: 'error',
            summary: 'Falha ao atualizar um pagamento',
            detail: error.error.message,
            life: 3000,
          }),
      });
  }

  public getPeopleList(): void {
    this._peopleService.get({ per_page: 0 }).subscribe({
      next: (response) => {
        this.peopleList.set(response.data);
      },
    });
  }

  public getContractList(peopleId: string): void {
    this._contractService
      .get({ take: 0 }, { people_id: peopleId, status: ContractStatus.ACTIVE })
      .subscribe({
        next: (response) => {
          this.contractList.set(response.data);
        },
      });
  }

  public getPaymentCategories(categoryId?: string): void {
    this._paymentCategoryService.get({ take: 0 }, { category_id: categoryId || null }).subscribe({
      next: (response) => {
        if (categoryId) {
          this.subcategoryList.set(response.data);
        } else {
          this.categoryList.set(response.data);
        }
      },
    });
  }

  public onDropFile(event: DragEvent, type: 'invoice' | 'doc'): void {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        this.handleFile(files[i], type);
      }
    }
  }

  public onFileSelected(event: Event, type: 'invoice' | 'doc'): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      for (let i = 0; i < input.files.length; i++) {
        this.handleFile(input.files[i], type);
      }
    }

    input.files = null;
  }

  private handleFile(file: File, type: 'invoice' | 'doc'): void {
    if (type === 'invoice') {
      this.paymentForm.get('invoice')?.setValue(file);
    }

    if (type === 'doc') {
      const docs = [...this.paymentDocs];

      docs.push(file);
      this.paymentForm.get('docs')?.setValue(docs);
    }
  }

  public removeDoc(index: number): void {
    const docs = [...this.paymentDocs];
    docs.splice(index, 1);
    this.paymentForm.get('docs')?.setValue(docs);
  }

  public removeInvoice(): void {
    this.paymentForm.get('invoice')?.setValue(null);
  }

  public openInvoice(): void {
    window.open(this.paymentForm.get('invoice_url')?.value, '_blank');
  }

  public openInvoiceDoc(id: number): void {
    const { invoiceDocs } = this;
    if (invoiceDocs && invoiceDocs[id]) {
      window.open(invoiceDocs[id].doc, '_blank');
    }
  }

  public openDoc(id: number): void {
    const docs = this.paymentDocs;

    if (docs[id]?.doc) {
      window.open(docs[id].doc, '_blank');
    }
  }

  public mountPaymentDueCurrentMonthMetrics(): any {
    return {
      labels: ['Atrasado', 'Pago', 'Em dia'],
      dataset: {
        data: [
          this.paymentDueMetrics()?.current_month?.overdue,
          this.paymentDueMetrics()?.current_month?.paid,
          this.paymentDueMetrics()?.current_month?.pending,
        ],
        backgroundColor: ['bg-rose-500', 'bg-emerald-300', 'bg-blue-500'],
      },
    };
  }

  public mountPaymentDueCurrentWeekMetrics(): any {
    return {
      labels: ['Atrasado', 'Pago', 'Em dia'],
      dataset: {
        data: [
          this.paymentDueMetrics()?.current_week?.overdue,
          this.paymentDueMetrics()?.current_week?.paid,
          this.paymentDueMetrics()?.current_week?.pending,
        ],
        backgroundColor: ['bg-rose-500', 'bg-emerald-300', 'bg-blue-500'],
      },
    };
  }

  public mountPaymentByCategoryData() {
    this.paymentByCategoryChartData.set({
      labels: this.paymentByCategory()?.categories.map((category: any) => category.name),
      datasets: [
        {
          label: '',
          data: this.paymentByCategory()?.categories.map((category: any) => category.value),
          backgroundColor: chartColors.slice(0, this.paymentByCategory()?.categories.length),
        },
      ],
    });
  }

  public mountPaymentEvolutionData(): void {
    const validData = this.paymentEvolution()?.months.slice(-6);
    if (!validData) {
      return;
    }

    this.paymentEvolutionChartData.set({
      labels: validData.map((month: PaymentEvolutionMonth) => month.month) || [],
      datasets: [
        {
          label: 'Fixos',
          data: validData.map((month: PaymentEvolutionMonth) => month.fixed) || [],
          backgroundColor: chartColors[0],
          borderWidth: 1,
          barPercentage: 0.7,
          categoryPercentage: 0.8,
          borderRadius: 6,
        },
        {
          label: 'Variáveis',
          data: validData.map((month: PaymentEvolutionMonth) => month.variable) || [],
          backgroundColor: chartColors[1],
          borderWidth: 1,
          barPercentage: 0.7,
          categoryPercentage: 0.8,
          borderRadius: 6,
        },
      ],
    });
  }

  public mountPaymentEvolutionOptions(): void {
    this.paymentEvolutionChartOptions.set({
      ...barChartDefaultOptions,
      maintainAspectRatio: false,
      scales: {
        ...barChartDefaultOptions?.scales,
        y: {
          stacked: true,
          grid: {
            display: true,
          },
          ticks: {
            callback: function (value, index, ticks) {
              return `R$ ${+value / 1000}K`;
            },
          },
          beginAtZero: true,
        },
        x: {
          stacked: true,
          grid: {
            display: false,
          },
        },
      },
      plugins: {
        ...barChartDefaultOptions?.plugins,
        tooltip: {
          ...barChartDefaultOptions?.plugins?.tooltip,
          callbacks: {
            label: (context: any) => {
              return [context.dataset.label, formatCurrency(context.raw, 'pt_BR', 'R$')];
            },
          },
        },
      },
    });
  }

  public mountTotalPaymentMetricData(): void {
    this.totalPaymentMetricsChartData.set({
      labels: ['Em dia', 'Total Pago', 'Total em Atraso'],
      datasets: [
        {
          label: '',
          data: [
            this.totalPaymentMetrics()?.unpaid_not_overdue.value,
            this.totalPaymentMetrics()?.total_paid.value,
            this.totalPaymentMetrics()?.total_overdue.value,
          ],
          backgroundColor: ['#6DA4FF', '#4FCF75', '#E03326'],
        },
      ],
    });
  }

  public mountTotalPaymentMetricOptions(): void {
    this.totalPaymentMetricsChartOptions.set({
      ...doughnutChartDefaultOptions,
      cutout: '70%',
    });
  }
}
